import { Input } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import ImageContainer from '../../ImageContainer';

interface IBasicInputNumberMask {
    id?: string;
    name: string;
    value?: string;
    placeholder?: string;       //eg. '(___) ___-____'
    mask: string;               //eg. '(000) 000-0000' (must use '0' to indicate where numbers go, everything else will be part of the mask)
    placeholderChar?: string;   //eg. ' ' or '_' (defaults to ' ')
    hideValue?: boolean;
    clearValue?: boolean;
    disabled?: boolean;
    maxLength?: number;
    tabIndex?: number;
    preventCopy?: boolean;
    onChange: (val: string) => void;
    useUnmaskedValue?: boolean;
    onFocus?: (ev: any) => void;
    innerIconName?: string;
    innerIconColor?: string;
}

const buildMaskLayer = (value: string, mask: string, placeholderChar: string) => {
    const numbers = value.replace(/\D/g, '');
    var maskedValue = '';

    for (var i = 0, j = 0; i < mask.length; i++) {
        if (mask[i] === '0') {
            if (numbers[j] !== '' && numbers[j] !== null && numbers[j] !== undefined) {
                maskedValue += numbers[j];
                j++;
            }
            else {
                maskedValue += placeholderChar[0];
            }
        }
        else {
            maskedValue += mask[i];
        }
    }

    return maskedValue;
}

const maxCursorPos = (value: string, mask: string) => {
    const unmaskedLength = value.replace(/\D/g, '').length;
    var cursorMax = 0;
    var numbersCounted = 0;

    while (numbersCounted < unmaskedLength && cursorMax < mask.length) {
        if (mask[cursorMax] === '0') {
            numbersCounted++;
        }
        cursorMax++;
    }
    return cursorMax;
}

const getHidingMask = (mask: string) => {
    return mask.replace(/\d/g, '*');
}

const BasicInputNumberMask: React.FC<IBasicInputNumberMask> = props => {
    const { id, name, value, placeholder, mask, placeholderChar, hideValue, clearValue, disabled, maxLength, tabIndex, preventCopy, onChange, useUnmaskedValue, onFocus, innerIconName, innerIconColor, ...rest } = props;
    const placeholderCharacter = placeholderChar[0] ?? ' ';
    const useUnmaskedInOnChange = useUnmaskedValue ?? false;

    const [unmasked, setUnmasked] = useState(value ? value.replace(/\D/g, '') : '');
    const [masked, setMasked] = useState(hideValue === true ? getHidingMask(mask) : '');
    const [keyPressed, setKeyPressed] = useState(undefined);
    const [cursorPos, setCursorPos] = useState(undefined);
    const ref = useRef(null);

    useEffect(() => {
        if (value !== null && value !== undefined && value.length > 0) {
            setMasked(buildMaskLayer(value, mask, placeholderCharacter))
        }
    }, [])

    useEffect(() => {
        if (hideValue === true) {
            setMasked(getHidingMask(mask));
        }
        else if (hideValue === false) {
            setMasked(buildMaskLayer(unmasked, mask, placeholderCharacter));
        }
    }, [hideValue])

    useEffect(() => {
        if (clearValue === true) {
            setUnmasked('');
            setMasked('');
        }
    }, [clearValue])

    const handleChange = () => {
        setCursorPos(ref.current.input.selectionStart);
        const maskedValue = buildMaskLayer(ref.current.input.value, mask, placeholderCharacter);
        ref.current.input.value = maskedValue;
        const numbers = ref.current.input.value.replace(/(\D)/g, '');
        setMasked(numbers === '' ? '' : maskedValue);
        setUnmasked(numbers);
        onChange(useUnmaskedInOnChange ? numbers : maskedValue);
    }

    const handleKeyDown = (e: any) => {
        setKeyPressed(e.key)
        var position = ref.current.input.selectionStart;
        var maxPosition = maxCursorPos(unmasked, mask);
        if (masked !== '' && e.key === 'Delete') {
            if (position === maxPosition) {
                e.preventDefault();
                e.stopPropagation();
            }
            else if (ref.current.input.selectionEnd === position) {
                while (mask[position] !== '0' && position < mask.length) {
                    position++;
                }
                ref.current.input.setSelectionRange(position, position);
            }
        }
        if (masked !== '' && e.key === 'Backspace') {
            if (position > maxPosition) {
                ref.current.input.setSelectionRange(maxPosition, maxPosition);
            }
        }
        if (masked === '' && (e.key === 'Backspace' || e.key === 'Delete')) {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    useEffect(() => {
        if (hideValue) return;
        var position = cursorPos;
        var maxPosition = maxCursorPos(unmasked, mask);

        if (unmasked.length === 0) {
            position = maxPosition;
        }
        else {
            if (keyPressed === "Backspace") {
                while (mask[position - 1] !== '0' && position > 0) {
                    position--;
                }
            }
            else if (keyPressed === "Delete") {
                //do nothing, position should be the same
            }
            else {
                position++;
                while (mask[position - 1] !== '0' && position < maxPosition) {
                    position++;
                }
            }
        }
        if (position > maxPosition) position = maxPosition;
        ref.current.input.setSelectionRange(position, position);
    }, [masked, unmasked, cursorPos])

    return <>
        <Input
            id={id}
            name={name}
            data-numbermaskinput={name || 'numbermaskinput'}
            placeholder={placeholder}
            className={'ant-input ant-input-outlined'}
            maxLength={maxLength}
            value={masked}
            onChange={handleChange}
            onFocus={onFocus}
            ref={ref}
            disabled={disabled}
            tabIndex={tabIndex ?? 0}
            onPaste={(e) => {
                preventCopy && e.preventDefault();
                return false;
            }}
            onCopy={(e) => {
                preventCopy && e.preventDefault();
                return false;
            }}
            onKeyDown={(e) => handleKeyDown(e)}
            {...rest}
        />
        {innerIconColor && innerIconName &&
            <ImageContainer iconName={innerIconName} className={`${innerIconColor} innerInputIcon`} />
        }
    </>
}

export default BasicInputNumberMask;
